import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { useToast } from "../../../provider/ToastProvider";
import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";
import { clearLoadingData } from "../../../redux/reducers/loadingReducer";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForUsers } from "../../../utils/confighelpers";
import { BookingsTab, PurchaseCard } from "../../classes/members/MemberTabs";
import { FcConfirmDialog } from "../../classes/schedule/ui";
import AccountTab from "../../settings/accountSettings/AccountTab";
import { clubIdOf, useClub, useMemberPurchases } from "./portalUtils";

/**
 * Member pages (new design), each its own menu item:
 *  - My Profile (/members/my-profile): the same AccountTab clubs use (photo, name, gender, date of birth via PUT users/updateProfile;
 *    username read-only; password change for non-social logins). Refreshes the user on open (GET users/getUserInfo).
 *  - My Purchases (/members/purchases): the member's own credit passes and subscriptions at the club, read-only.
 *    /members/purchases/success is where Stripe Checkout returns after a purchase (Club Webshop): a confirmation,
 *    and the list refreshes every 5 s (up to a minute) until the new purchase shows (it is recorded by the Stripe webhook).
 *  - My Bookings (/members/bookings): getMybookings for the member at the club; upcoming ones can be cancelled
 *    (POST cancelClassBooking, same body as the old member page, not within 3 hours of the start).
 */

const SECTIONS = {
  profile: { title: "My Profile", line: (club) => `Your details at ${club}.` },
  purchases: { title: "My Purchases", line: (club) => `Your credit passes and subscriptions at ${club}.` },
  bookings: { title: "My Bookings", line: (club) => `Your classes at ${club}.` },
};
const POLL_EVERY_MS = 5000;
const POLL_MAX = 12;

function Section({ title, items, empty, action }) {
  return (
    <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
      <h3 className="text-[17px] font-black text-gray-900">
        {title} <span className="text-[15px] font-bold text-gray-500">({items.length})</span>
      </h3>
      <div className="mt-4 flex flex-col gap-2.5">
        {items.length ? (
          items.map((p) => <PurchaseCard key={p._id} p={p} />)
        ) : (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border-[1.5px] border-dashed border-[#E3DCE6] bg-[#FCFBFD] px-4 py-3.5 text-sm text-gray-600">
            <span>{empty}</span>
            {action}
          </div>
        )}
      </div>
    </section>
  );
}

export default function MemberAccountPage({ section = "profile", justBought = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const dashboardReduxState = useSelector(dashboardState);
  const club = useClub();
  const me = userReduxState.userData?._id;
  const clubId = clubIdOf(userReduxState.userData);
  const tab = SECTIONS[section] ? section : "profile";
  const purchases = useMemberPurchases();
  // After Stripe Checkout: the Stripe webhook records the purchase, usually within seconds.
  // "Arrived" = a purchase made in the last 15 minutes is in the list; until then refresh every 5 s (max 1 minute).
  const [polls, setPolls] = React.useState(0);
  const arrived =
    justBought &&
    !purchases.loading &&
    [...purchases.classpass, ...purchases.subscription, ...purchases.free].some((p) => Date.now() - new Date(p.purchaseDate || p.createdAt).getTime() < 15 * 60 * 1000);
  const waiting = justBought && !purchases.loading && !arrived && polls < POLL_MAX;
  const [cancelling, setCancelling] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    dispatch(clearLoadingData());
    if (userReduxState.userData?.userType && userReduxState.userData.userType !== "student") {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
        if (res?.data?.statusCode === 200) dispatch(userActions.setUserData({ ...userReduxState.userData, ...res.data.data }));
      } catch (e) {
        /* keep what we have */
      }
    })(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!waiting) return undefined;
    const t = setTimeout(() => {
      setPolls((n) => n + 1);
      dispatch(dashboardActions.setUpdateSessionComp((dashboardReduxState.updateSessionComp || 0) + 1));
    }, POLL_EVERY_MS);
    return () => clearTimeout(t); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, polls]);

  const cancelBooking = async () => {
    const b = cancelling;
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}cancelClassBooking`,
        data: { bookingId: b.bookingId, scheduleClassId: b.scheduleClassId, type: "remove", isPackage: b.isPackage, participantId: b.studentId, familyMemberId: "" },
      });
      if (res.status === 200 && res.data.statusCode === 200) {
        addSuccessToast("Your booking is cancelled");
        setCancelling(null);
        dispatch(dashboardActions.setUpdateSessionComp(dashboardReduxState.updateSessionComp + 1));
      } else addErrorToast((res.data && typeof res.data.message === "string" && res.data.message) || "Couldn't cancel this booking.");
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const toShop = (
    <button type="button" onClick={() => navigate("/members/webshop")} className="inline-flex h-9 items-center rounded-xl bg-[#F3ECF5] px-3.5 text-sm font-extrabold text-brown-coffee transition-colors hover:bg-brown-coffee hover:text-white">
      Go to Club Webshop
    </button>
  );

  return (
    <TwMainLayout
      userType={"student"}
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-5xl">
            <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">{SECTIONS[tab].title}</h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">{SECTIONS[tab].line(club.name)}</p>

            {tab === "purchases" && justBought && (
              <div role="status" className="mt-5 flex items-start gap-3 rounded-2xl border border-[#BFE6CD] bg-[#E8F7EE] px-4 py-3.5">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F6B3A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0" aria-hidden="true">
                  <path d="m5 12 5 5 9-10" />
                </svg>
                <div className="text-sm text-[#14532D]">
                  <div className="font-extrabold">Thank you, your payment went through.</div>
                  <div className="mt-0.5 flex items-center gap-2">
                    {arrived ? (
                      "Your new pass is below. You can book classes with it now."
                    ) : waiting ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#1F6B3A]/30 border-t-[#1F6B3A]" aria-hidden="true" />
                        Adding it to your account, this takes a few seconds…
                      </>
                    ) : (
                      "It can take a minute to show here. Refresh the page if you don't see it yet."
                    )}
                  </div>
                </div>
                {arrived && (
                  <button type="button" onClick={() => navigate("/members/schedule")} className="ml-auto inline-flex h-9 shrink-0 items-center rounded-xl bg-[#1F6B3A] px-3.5 text-sm font-extrabold text-white hover:bg-[#14532D]">
                    Book a class
                  </button>
                )}
              </div>
            )}

            <div className="mt-5">
              {tab === "profile" && <AccountTab profileSubtitle={`How ${club.name} sees you.`} />}

              {tab === "purchases" &&
                (purchases.loading ? (
                  <div className="flex flex-col gap-4" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-36 animate-pulse rounded-3xl bg-[#F3EEF5]" />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <Section title="Credit passes" items={purchases.classpass} empty="No credit pass yet." action={toShop} />
                    <Section title="Subscriptions" items={purchases.subscription} empty="No subscription yet." action={toShop} />
                    {purchases.free.length > 0 && <Section title="Free credits" items={purchases.free} empty="" />}
                  </div>
                ))}

              {tab === "bookings" && (
                <BookingsTab consumerId={me} businessId={clubId} memberView onCancel={(b) => setCancelling(b)} refreshKey={dashboardReduxState.updateSessionComp} />
              )}
            </div>
          </div>

          <FcConfirmDialog
            open={!!cancelling}
            title="Cancel this booking?"
            message={cancelling ? `${cancelling.className || cancelling.class_detail?.className || "This class"} will be removed from your bookings.` : ""}
            confirmLabel="Cancel booking"
            cancelLabel="Keep it"
            danger
            busy={busy}
            onConfirm={cancelBooking}
            onClose={() => setCancelling(null)}
          />
        </div>
      }
    />
  );
}
